/* CUZY NYSST — coverflow carousel. Vanilla JS, no build step, no dependencies.
   Scoped entirely to its own <cuzy-coverflow> element — safe to place on the
   same page as many other instances (product cards, multiple sections, etc.)
   since nothing here touches global ids or classes outside itself. */
(function () {
  console.log('[cuzy-coverflow] script running (build v3)');

  function register() {
    if (customElements.get('cuzy-coverflow')) return;

    class CuzyCoverflow extends HTMLElement {
      connectedCallback() {
        this.init();
      }

      init() {
        this.cards = Array.from(this.querySelectorAll('.cuzy-cf-card'));
        this.caps = Array.from(this.querySelectorAll('[data-cap]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.count = this.cards.length;
        if (!this.count) return;

        if (!this.__stateInit) {
          this.__stateInit = true;
          this.pos = 0;
          this.target = 0;
          this.raf = null;
          this.drag = null;
        }

        this.stage = this.querySelector('.cuzy-cf-stage');

        this.cards.forEach((card) => {
          if (card.__cuzyBound) return;
          card.__cuzyBound = true;
          card.addEventListener('pointerdown', (e) => this.onDown(e, card));
          card.addEventListener('click', (e) => this.onClick(e, card));
          card.addEventListener('dragstart', (e) => e.preventDefault());
        });
        this.dots.forEach((dot, i) => {
          if (dot.__cuzyBound) return;
          dot.__cuzyBound = true;
          dot.addEventListener('click', () => this.goTo(i));
        });

        this.paint();
      }

      disconnectedCallback() {
        if (this.raf) cancelAnimationFrame(this.raf);
      }

      measure() {
        this.paint();
      }

      centerWidth() {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const varName = isMobile ? '--cuzy-cf-center-w-mobile' : '--cuzy-cf-center-w';
        const fallback = isMobile ? 250 : 300;
        const value = parseFloat(getComputedStyle(this).getPropertyValue(varName));
        return (value && !isNaN(value)) ? value : fallback;
      }

      dragPitch() {
        return 130;
      }

      indexAt(pos) {
        return ((Math.round(pos) % this.count) + this.count) % this.count;
      }

      paint() {
        const centerW = this.centerWidth();
        const pitch = centerW * 0.6;
        const tzMax = centerW * 0.5;
        const pos = this.pos;

        this.cards.forEach((card, i) => {
          let offset = i - pos;
          offset = ((offset % this.count) + this.count) % this.count;
          if (offset > this.count / 2) offset -= this.count;

          const distance = Math.abs(offset);
          const ramp = Math.min(Math.pow(distance, 0.6), 1.8);
          const tilt = Math.min(34 * ramp, 80) * Math.sign(offset);
          const depth = tzMax * ramp;

          card.style.transform =
            `translate(-50%, -50%) translateX(${offset * pitch}px) ` +
            `translateZ(${-depth}px) rotateY(${-tilt}deg)`;
          card.style.opacity = String(Math.max(0, 1 - 0.5 * distance));
          card.style.zIndex = String(100 - Math.round(distance));
          card.style.width = distance < 0.5
            ? `${centerW}px`
            : '';
        });
      }

      updateActive(index) {
        this.caps.forEach((cap, i) => {
          cap.style.display = i === index ? 'flex' : 'none';
        });
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      }

      settle(target) {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.target = target;
        this.updateActive(this.indexAt(target));

        const step = () => {
          const remaining = target - this.pos;
          if (Math.abs(remaining) < 0.0005) {
            this.pos = target;
            this.paint();
            this.raf = null;
            return;
          }
          this.pos += remaining * 0.18;
          this.paint();
          this.raf = requestAnimationFrame(step);
        };
        this.raf = requestAnimationFrame(step);
      }

      goTo(index) {
        const target = index + Math.round((this.target - index) / this.count) * this.count;
        this.settle(target);
      }

      nudge(by) {
        this.settle(Math.round(this.target) + by);
      }

      onDown(e, card) {
        if (this.raf) {
          cancelAnimationFrame(this.raf);
          this.raf = null;
        }
        card.setPointerCapture(e.pointerId);
        this.target = this.pos;
        this.suppressClick = false;
        this.drag = { id: e.pointerId, x: e.clientX, pos: this.pos, v: 0, t: performance.now() };

        const onMove = (ev) => {
          const d = this.drag;
          if (!d || d.id !== ev.pointerId) return;
          const pitch = this.dragPitch();
          const dx = ev.clientX - d.x;
          if (Math.abs(dx) > 4) this.suppressClick = true;
          const now = performance.now();
          const prev = this.pos;
          this.pos = d.pos - dx / pitch;
          d.v = ((this.pos - prev) / Math.max(now - d.t, 1)) * 1000;
          d.t = now;
          this.updateActive(this.indexAt(this.pos));
          this.paint();
        };

        const onUp = (ev) => {
          const d = this.drag;
          if (!d || d.id !== ev.pointerId) return;
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          this.drag = null;
          if (this.suppressClick) {
            const carried = Math.max(-2, Math.min(2, d.v * 0.15));
            this.settle(Math.round(this.pos + carried));
          }
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      }

      onClick(e, card) {
        if (this.suppressClick) {
          this.suppressClick = false;
          e.preventDefault();
          return;
        }
        const index = parseInt(card.dataset.index, 10);
        this.goTo(index);
      }
    }

    customElements.define('cuzy-coverflow', CuzyCoverflow);
  }

  register();

  function initWithin(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('cuzy-coverflow').forEach((el) => {
      if (typeof el.init === 'function') el.init();
    });
  }

  document.addEventListener('shopify:section:load', (e) => initWithin(e.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document));
  } else {
    initWithin(document);
  }

  setInterval(() => initWithin(document), 500);
})();
